import React, { useCallback, useEffect, useRef, useState } from 'react';
import useMeasure from 'react-use-measure';
import {
  XMarkIcon,
  PlayIcon,
  PauseIcon,
  ForwardIcon,
  BackwardIcon,
  ArrowPathIcon,
  ChevronUpIcon,
} from '@heroicons/react/24/outline';

import { Play } from '../types';
import { TransitionMode } from '../store/app';
import { usePlaybackEngine } from '../hooks/usePlaybackEngine';
import { PoolElement } from '../Pool';
import {
  Backdrop,
  ModalContainer,
  Header,
  PlayName,
  CloseButton,
  PoolWrapper,
  ControlsBar,
  ControlBtn,
  ProgressText,
  ProgressBarContainer,
  ProgressBarFill,
  ModeToggleContainer,
  ModeLabel,
  ModeButton,
  FrameStripContainer,
  FrameStripItem,
  FrameStripLabel,
  FrameStripToggleBtn,
} from './PlayPlayer.style';

export interface PlayPlayerProps {
  play: Play;
  isOpen: boolean;
  onClose: () => void;
}

export const PlayPlayer: React.FC<PlayPlayerProps> = ({
  play,
  isOpen,
  onClose,
}) => {
  const [animationMode, setAnimationMode] = useState<TransitionMode>(
    TransitionMode.TIME_BASED,
  );
  const [isFrameStripOpen, setIsFrameStripOpen] = useState(false);
  const [poolWrapperRef, { width: poolMaxWidth, height: poolMaxHeight }] =
    useMeasure();

  const snapshots = play.snapshots || [];

  const {
    currentSnapshot,
    isPlaying,
    isFinished,
    currentIndex,
    totalSnapshots,
    play: startPlay,
    pause,
    nextFrame,
    prevFrame,
    reset,
    goToFrame,
  } = usePlaybackEngine(snapshots, animationMode);

  const frameStripRef = useRef<HTMLDivElement>(null);

  const handleBackdropClick = useCallback(
    (e: React.MouseEvent<HTMLDivElement>) => {
      if (e.target === e.currentTarget) {
        onClose();
      }
    },
    [onClose],
  );

  const handlePlayPause = useCallback(() => {
    if (isPlaying) {
      pause();
    } else {
      startPlay();
    }
  }, [isPlaying, pause, startPlay]);

  const handleNextFrame = useCallback(() => {
    if (!isPlaying) {
      nextFrame();
    }
  }, [isPlaying, nextFrame]);

  const handlePrevFrame = useCallback(() => {
    if (!isPlaying) {
      prevFrame();
    }
  }, [isPlaying, prevFrame]);

  const handleReset = useCallback(() => {
    reset();
  }, [reset]);

  const handleFrameClick = useCallback(
    (index: number) => {
      pause();
      goToFrame(index);
    },
    [pause, goToFrame],
  );

  // Auto-scroll the frame strip so the active frame stays visible
  useEffect(() => {
    if (frameStripRef.current) {
      const container = frameStripRef.current;
      const activeItem = container.children[currentIndex] as
        | HTMLElement
        | undefined;
      if (activeItem) {
        const scrollLeft =
          activeItem.offsetLeft -
          container.offsetWidth / 2 +
          activeItem.offsetWidth / 2;
        container.scrollTo({ left: scrollLeft, behavior: 'smooth' });
      }
    }
  }, [currentIndex]);

  // Progress calculation
  const progress =
    totalSnapshots > 1 ? (currentIndex / (totalSnapshots - 1)) * 100 : 0;

  const isAtStart = currentIndex === 0;
  const isAtEnd = currentIndex === totalSnapshots - 1;
  const canGoNext = !isPlaying && !isAtEnd;
  const canGoPrev = !isPlaying && !isAtStart;
  const canReset = !isAtStart || isFinished;
  const hasMultipleSnapshots = totalSnapshots > 1;

  if (!isOpen) return null;

  return (
    <Backdrop $isOpen={isOpen} onClick={handleBackdropClick}>
      <ModalContainer onClick={(e) => e.stopPropagation()}>
        <Header>
          <PlayName>{play.name}</PlayName>
          <CloseButton onClick={onClose}>
            <XMarkIcon />
          </CloseButton>
        </Header>

        <PoolWrapper ref={poolWrapperRef}>
          {currentSnapshot && poolMaxWidth > 0 && poolMaxHeight > 0 && (
            <PoolElement
              halfView={true}
              poolMaxHeight={poolMaxHeight - 40}
              poolMaxWidth={poolMaxWidth - 40}
              snapshot={currentSnapshot}
              isStatic={true}
              isDrawingEnabled={false}
              drawingMode={0}
              shouldCleanDrawings={false}
              onDrawingsCleaned={() => {}}
              shouldUndoLastDrawing={false}
              onLastDrawingUndone={() => {}}
              opponentIncrease={100}
              namings={play.namings}
              playName={play.name}
            />
          )}
        </PoolWrapper>

        {hasMultipleSnapshots && (
          <ControlsBar $frameStripOpen={isFrameStripOpen}>
            <ControlBtn
              $isDisabled={!canGoPrev}
              onClick={canGoPrev ? handlePrevFrame : undefined}
              title="Previous frame"
            >
              <BackwardIcon />
            </ControlBtn>

            <ControlBtn
              onClick={handlePlayPause}
              $isActive={isPlaying}
              title={isPlaying ? 'Pause' : 'Play'}
            >
              {isPlaying ? <PauseIcon /> : <PlayIcon />}
            </ControlBtn>

            <ControlBtn
              $isDisabled={!canGoNext}
              onClick={canGoNext ? handleNextFrame : undefined}
              title="Next frame"
            >
              <ForwardIcon />
            </ControlBtn>

            <ControlBtn
              $isDisabled={!canReset}
              onClick={canReset ? handleReset : undefined}
              title="Reset"
            >
              <ArrowPathIcon />
            </ControlBtn>

            <ProgressBarContainer>
              <ProgressBarFill $progress={progress} />
            </ProgressBarContainer>

            <ProgressText>
              {currentIndex + 1} / {totalSnapshots}
            </ProgressText>

            <FrameStripToggleBtn
              $isOpen={isFrameStripOpen}
              onClick={() => setIsFrameStripOpen(!isFrameStripOpen)}
              title={isFrameStripOpen ? 'Hide frames' : 'Show frames'}
            >
              <ChevronUpIcon />
            </FrameStripToggleBtn>

            <ModeToggleContainer>
              <ModeLabel>
                Playback
                <br />
                mode:
              </ModeLabel>
              <ModeButton
                $isActive={animationMode === TransitionMode.TIME_BASED}
                onClick={() => setAnimationMode(TransitionMode.TIME_BASED)}
              >
                Time
              </ModeButton>
              <ModeButton
                $isActive={animationMode === TransitionMode.DISTANCE_BASED}
                onClick={() => setAnimationMode(TransitionMode.DISTANCE_BASED)}
              >
                Realistic
              </ModeButton>
            </ModeToggleContainer>
          </ControlsBar>
        )}

        {hasMultipleSnapshots && (
          <FrameStripContainer ref={frameStripRef} $isOpen={isFrameStripOpen}>
            {snapshots.map((snapshot, index) => (
              <FrameStripItem
                key={`frame-strip-${index}`}
                $isActive={index === currentIndex}
                onClick={() => handleFrameClick(index)}
              >
                <PoolElement
                  halfView={true}
                  poolMaxHeight={65}
                  poolMaxWidth={120}
                  snapshot={snapshot}
                  isStatic={true}
                  isDrawingEnabled={false}
                  drawingMode={0}
                  shouldCleanDrawings={false}
                  onDrawingsCleaned={() => {}}
                  shouldUndoLastDrawing={false}
                  onLastDrawingUndone={() => {}}
                  opponentIncrease={100}
                  backgroundText={`${index + 1}`}
                />
                <FrameStripLabel>{index + 1}</FrameStripLabel>
              </FrameStripItem>
            ))}
          </FrameStripContainer>
        )}
      </ModalContainer>
    </Backdrop>
  );
};
